// A boat's order file (Info / Files › New order file): the yard's order template as a form, English with the
// Greek beside it, and the boat's BOM worked out from it as it is filled in. The form's cells are the boat's
// Order Sheet row (so the Order Sheet, its Procurement tab and the Warehouse rollup follow); a new order
// file starts with the model's standard equipment ticked. The BOM is the model's standard sections plus the
// option sections the order calls for (BOM lists page rules), and any section can be ticked in or out by hand
// for this boat. Saving keeps all that and puts two workbooks in the season's Info / Files folder for the model,
// where the crews open them: the order (the form, a picking list by box, a SAP sheet) and the BOM, which is the
// model's sheet of the STD lists exactly as uploaded with the sections the boat hasn't got deleted.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ORDER_FILE_FIELDS,
  ORDER_FILE_TEMPLATE,
  ORDER_FILE_TICK,
  MODEL_ORDER,
  OS_MODEL_KEY,
  OS_NAME_KEY,
  bomLines,
  bomLinkText,
  modelKey,
  orderFileName,
  orderKeySap,
  orderValueOn,
  partTotals,
  resolveBom,
  type ResolvedBomSection,
} from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { useSeason } from '../season/SeasonContext.js';
import { useOrderSheet, useOverview } from '../features/hooks.js';
import { bomSeasonFor, fetchBomSheet, useBom, useBomList, useOrderFiles, useSaveOrderFile, useStoreOrderWorkbook } from '../features/bom.js';
import { seasonFolderLabel } from '../features/orderList.js';
import { useNameResolver } from '../features/directory.js';
import { EmptyState, ErrorState, InlineError, LoadingState, PageHeader } from '../components/ui.js';
import { IconChevronRight } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';

const ORDER_LISTS_PREFIX = 'order-lists/';

export function OrderFile() {
  const { sap = '' } = useParams();
  const [params] = useSearchParams();
  const { activeSeason } = useSeason();
  const season = params.get('season') || activeSeason;
  const { can } = useAuth();
  const canWrite = can('production:write');
  const df = useDateFormatter();
  const nameOf = useNameResolver();

  const overview = useOverview(season || undefined);
  const sheetQ = useOrderSheet();
  const filesQ = useOrderFiles(season);
  const bomQ = useBom();
  const vessel = overview.data?.vessels.find((v) => v.sap === sap);
  const record = filesQ.data?.find((f) => f.sap === sap);
  // a boat that isn't on the Overview (a stock boat, one not in Smartsheet yet): its name and model come from
  // its order file, its Order Sheet row, or what was typed when it was started, and can be changed here
  const row = sheetQ.data?.sheet[sap];
  const listed = Boolean(vessel);
  const [ident, setIdent] = useState<{ name: string; model: string } | null>(null);
  const name = vessel?.name ?? ident?.name ?? record?.name ?? row?.[OS_NAME_KEY] ?? params.get('name') ?? '';
  const rawModel = vessel?.model ?? ident?.model ?? record?.model ?? row?.[OS_MODEL_KEY] ?? params.get('model') ?? '';
  const model = rawModel ? modelKey(rawModel) : '';
  const sapShown = orderKeySap(sap);
  const bomSeason = bomQ.data ? bomSeasonFor(bomQ.data.lists, model, season) : '';
  const listQ = useBomList(bomSeason, model, Boolean(bomSeason));
  const links = bomQ.data?.config.links[model] ?? {};
  const std = useMemo(() => new Set(bomQ.data?.config.std[model] ?? []), [bomQ.data, model]);

  // the form: the boat's Order Sheet row; a first order file also ticks the model's standard equipment. `base` is
  // the row as it was read: a save sends only what was changed from it, so it never undoes a change someone
  // else made to the boat's row (in the Order Sheet, or another order file) meanwhile.
  const [values, setValues] = useState<Record<string, string> | null>(null);
  const [base, setBase] = useState<Record<string, string>>({});
  const [picks, setPicks] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const known = listed || Boolean(record || row || params.get('name'));
  const ready = Boolean(overview.data && known && sheetQ.data && filesQ.data && bomQ.data);
  useEffect(() => {
    if (!ready || values) return;
    const row = sheetQ.data!.sheet[sap] ?? {};
    const start: Record<string, string> = {};
    for (const f of ORDER_FILE_FIELDS) start[f.key] = row[f.key] ?? '';
    setBase({ ...start });
    if (!record) for (const k of std) if (!orderValueOn(start[k])) start[k] = ORDER_FILE_TICK;
    setValues(start);
    setPicks(record?.picks ?? {});
  }, [ready, values, sheetQ.data, sap, record, std]);

  const set = (key: string, v: string) => { setValues((p) => ({ ...(p ?? {}), [key]: v })); setDirty(true); };
  const pick = (key: string, on: boolean | undefined) => {
    setPicks((p) => { const n = { ...p }; if (on === undefined) delete n[key]; else n[key] = on; return n; });
    setDirty(true);
  };

  const resolved = useMemo(() => (listQ.data && values ? resolveBom(listQ.data, values, links, picks) : []), [listQ.data, values, links, picks]);
  const lines = useMemo(() => bomLines(resolved), [resolved]);
  const parts = useMemo(() => partTotals([lines]), [lines]);

  const save = useSaveOrderFile();
  const store = useStoreOrderWorkbook();
  const [msg, setMsg] = useState('');
  const busy = save.isPending || store.isPending;
  const folder = `${ORDER_LISTS_PREFIX}${seasonFolderLabel(season)}/${model}/`;
  const fileName = orderFileName(sapShown, name);
  const bomName = fileName.replace(/\.xlsx$/, ' - BOM.xlsx');
  const canSave = Boolean(name.trim() && model);
  const setIdentity = (p: Partial<{ name: string; model: string }>) => { setIdent({ name, model, ...p }); setDirty(true); };
  const bomFrom = listQ.data ? `${listQ.data.source || 'STD lists'}, season ${listQ.data.season}` : 'No BOM list';

  // the order and its BOM from given values (the ones just saved), else from the form's
  const resolvedFor = (v?: Record<string, string>) => (v && listQ.data ? resolveBom(listQ.data, v, links, picks) : resolved);
  const workbook = async (stamp: string, v?: Record<string, string>) => {
    const { buildOrderWorkbook } = await import('../features/orderFileExport.js');
    // (a boat with no SAP yet shows none, not the key it is kept under)
    return buildOrderWorkbook({ sap: sapShown, name, model, values: v ?? values ?? {}, lines: v ? bomLines(resolvedFor(v)) : lines, stamp });
  };
  // the boat's BOM: the model's STD lists sheet as it was uploaded, with the sections it hasn't got deleted
  const bomWorkbook = async (v?: Record<string, string>): Promise<Blob> => {
    if (!listQ.data) throw new Error(`There’s no BOM list for the ${model} yet.`);
    // the list as it is now: one uploaded again while this page was open goes with another sheet, whose rows
    // these sections don't match (the page takes the new list; making the BOM again uses it)
    const now = (await listQ.refetch()).data;
    if (now && now.uploadedAt !== listQ.data.uploadedAt) throw new Error('The BOM list was uploaded again while this page was open. Make the BOM again to take the new one.');
    const resolved = resolvedFor(v);
    if (resolved.some((r) => !r.section.rows)) throw new Error('This BOM list was uploaded before BOMs were made from the STD lists sheet: upload the STD lists again on the BOM lists page.');
    const [source, { rowsToDelete, stdSheetWorkbook }, { XLSX_TYPE }] = await Promise.all([
      fetchBomSheet(listQ.data.season, model),
      import('../features/stdSheet.js'),
      import('../features/orderFileExport.js'),
    ]);
    const deleteRows = rowsToDelete(resolved.map((r) => ({ rows: r.section.rows, groupRows: r.section.groupRows, included: r.included })));
    return new Blob([stdSheetWorkbook(source, { deleteRows })], { type: XLSX_TYPE });
  };
  const [bomBusy, setBomBusy] = useState(false);
  const [bomError, setBomError] = useState<unknown>(null);

  const onSave = async () => {
    if (!values) return;
    setMsg('');
    let saved = false;
    try {
      const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => (base[k] ?? '') !== v));
      const file = await save.mutateAsync({ sap, season, model, name: name.trim(), cells: changed, picks, unlisted: !listed });
      saved = true;
      setDirty(false);
      // the boat's row as the Order Sheet has it now (anyone else's changes since this page opened, these on
      // top): the form, the workbook and the BOM follow it
      const fresh = (await sheetQ.refetch()).data?.sheet[sap] ?? {};
      const now: Record<string, string> = {};
      for (const f of ORDER_FILE_FIELDS) now[f.key] = f.key in changed ? changed[f.key]! : fresh[f.key] ?? values[f.key] ?? '';
      setValues(now);
      setBase({ ...now });
      const stamp = `Revision ${file.revision} · ${df.dateTime(file.updatedAt)} · ${nameOf(file.updatedBy)}`;
      const blob = await workbook(stamp, now);
      // the BOM goes with it when it can be made; the order is saved either way
      let bom: Blob | null = null;
      let bomWhy = '';
      if (listQ.data) {
        try { bom = await bomWorkbook(now); } catch (e) { bomWhy = e instanceof Error ? e.message : 'It couldn’t be made.'; }
      }
      const key = await store.mutateAsync({
        sap,
        prefix: folder,
        file: new File([blob], fileName, { type: blob.type }),
        with: bom ? [new File([bom], bomName, { type: bom.type })] : [],
      });
      // files no longer this order's: the last revision's pair under the boat's old name (or model), and a BOM
      // this revision couldn't make again (crews would open one that doesn't match the order)
      const old = record?.fileKey ?? '';
      const stale = [
        ...(old && old !== key ? [old, old.replace(/\.xlsx$/, ' - BOM.xlsx')] : []),
        ...(!bom ? [key.replace(/\.xlsx$/, ' - BOM.xlsx')] : []),
      ];
      await Promise.all(stale.map((k) => api.post('/api/files/delete', { key: k }).catch(() => undefined)));
      const where = `Info / Files › ${seasonFolderLabel(season)} › ${model}`;
      setMsg(`Saved revision ${file.revision}. The Order Sheet is updated and ${bom ? `${fileName} and ${bomName} are` : `${fileName} is`} in ${where}.${bomWhy ? ` The BOM wasn’t made: ${bomWhy}` : ''}`);
    } catch {
      // a failed save shows its own error below; a failed upload after it is said here
      if (saved) setMsg('The order is saved and the Order Sheet updated, but the workbook couldn’t be put in Info / Files. Try saving again, or download it.');
    }
  };

  const onDownload = async () => {
    const { saveBlob } = await import('../features/orderFileExport.js');
    saveBlob(await workbook(dirty ? 'Not saved yet' : record ? `Revision ${record.revision}` : 'Not saved yet'), fileName);
  };
  const onBom = async () => {
    setBomBusy(true);
    setBomError(null);
    try {
      const blob = await bomWorkbook();
      const { saveBlob } = await import('../features/orderFileExport.js');
      saveBlob(blob, bomName);
    } catch (e) {
      setBomError(e);
    } finally {
      setBomBusy(false);
    }
  };
  const onSapCsv = async () => {
    const { downloadSapCsv } = await import('../features/orderFileExport.js');
    downloadSapCsv(fileName.replace(/\.xlsx$/, ' - SAP.csv'), parts);
  };

  const err = overview.error ?? sheetQ.error ?? filesQ.error ?? bomQ.error;
  if (err) return <><Back /><ErrorState error={err} /></>;
  if (overview.data && sheetQ.data && filesQ.data && !known) {
    return (
      <>
        <Back />
        <PageHeader title="Order file" />
        <EmptyState title={`No boat with SAP ${sapShown || sap} in season ${season}`} hint="Pick the boat again from Info / Files › + Order file, or start it there as a new boat." />
      </>
    );
  }
  if (!values) return <><Back /><LoadingState /></>;

  const included = resolved.filter((r) => r.included);
  const stdCount = included.filter((r) => r.section.part === 'std').length;
  const optCount = included.length - stdCount;

  return (
    <>
      <Back />
      <PageHeader title={`Order file · ${name || sapShown || 'new boat'}`} meta={<span className="muted">{model || 'No model'} · {sapShown ? `SAP ${sapShown}` : 'no SAP yet'} · season {season}</span>}>
        {!listed && <span className="pill" title="This boat isn’t on the season’s Overview: its name and model are kept on the Order Sheet">Not on the Overview</span>}
        {dirty && <span className="pill warn" title="Changes not saved yet">Not saved</span>}
        {record && !dirty && <span className="muted of-rev" title={`Saved ${df.dateTime(record.updatedAt)} by ${nameOf(record.updatedBy)}`}>Revision {record.revision}</span>}
        <button className="btn btn-sm" onClick={() => void onDownload()} title="The order workbook as it stands, without saving">Download</button>
        <button className="btn btn-sm" disabled={!listQ.data || bomBusy} onClick={() => void onBom()} title="The boat’s BOM: the model’s STD lists sheet with the sections it hasn’t got deleted, as it stands">{bomBusy ? 'BOM…' : 'BOM'}</button>
        <button className="btn btn-sm" disabled={!parts.length} onClick={() => void onSapCsv()} title="Part code and quantity, for loading into SAP">SAP file</button>
        {canWrite && <button className="btn btn-sm btn-primary" disabled={busy || !canSave} title={canSave ? undefined : 'Give the boat a name and a model first'} onClick={() => void onSave()}>{busy ? 'Saving…' : 'Save'}</button>}
      </PageHeader>
      <div className="muted" role="status" style={msg ? { marginBottom: 10 } : undefined}>{msg}</div>
      {(save.error ?? store.error) && <InlineError error={save.error ?? store.error} onDismiss={() => { save.reset(); store.reset(); }} />}
      {bomError != null && <InlineError error={bomError} onDismiss={() => setBomError(null)} />}

      <div className="of-layout">
        <section className="of-form card" aria-label="Order">
          {listed || !canWrite ? (
            <>
              <div className="of-idrow"><span className="of-en">MODEL</span><b>{model}</b><span className="of-gr">ΜΟΝΤΕΛΟ</span></div>
              <div className="of-idrow"><span className="of-en">NAME</span><b>{name}</b><span className="of-gr">ΟΝΟΜΑ</span></div>
            </>
          ) : (
            <>
              <div className="of-row of-text">
                <span className="of-en">MODEL</span>
                <select className="field of-val" aria-label="Model (ΜΟΝΤΕΛΟ)" value={model} onChange={(e) => setIdentity({ model: e.target.value })}>
                  <option value="">Choose…</option>
                  {MODEL_ORDER.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <span className="of-gr">ΜΟΝΤΕΛΟ</span>
              </div>
              <div className="of-row of-text">
                <span className="of-en">NAME</span>
                <input className="field of-val" aria-label="Name (ΟΝΟΜΑ)" value={name} onChange={(e) => setIdentity({ name: e.target.value.toUpperCase() })} />
                <span className="of-gr">ΟΝΟΜΑ</span>
              </div>
            </>
          )}
          <div className="of-idrow"><span className="of-en">SAP</span><b>{sapShown || <span className="muted">Not yet</span>}</b><span className="of-gr">SAP</span></div>
          {ORDER_FILE_TEMPLATE.map((r) => {
            if (r.kind === 'band') return <h2 key={r.key} className="of-band">{r.en.replace(/:$/, '')}</h2>;
            if (r.kind === 'section') return <h3 key={r.key} className="of-section">{r.en.replace(/:$/, '')}{r.gr && <span className="of-gr"> · {r.gr.replace(/:$/, '')}</span>}</h3>;
            const v = values[r.key] ?? '';
            const label = r.en.replace(/:$/, '');
            const isStd = std.has(r.key);
            return (
              <div key={r.key} className={`of-row of-${r.kind}${orderValueOn(v) ? ' is-on' : ''}`}>
                {r.kind === 'option' ? (
                  <label className="of-tick">
                    <input type="checkbox" checked={orderValueOn(v)} disabled={!canWrite} onChange={(e) => set(r.key, e.target.checked ? ORDER_FILE_TICK : '')} />
                    <span className="of-en">{label}</span>
                  </label>
                ) : <span className="of-en">{label}</span>}
                {isStd && <span className="of-std" title={`Standard on the ${model}`}>STD</span>}
                <input
                  className="field of-val"
                  value={v}
                  disabled={!canWrite}
                  aria-label={`${label}${r.gr ? ` (${r.gr})` : ''}`}
                  placeholder={r.kind === 'option' ? 'Note' : ''}
                  onChange={(e) => set(r.key, e.target.value)}
                />
                <span className="of-gr">{r.gr}</span>
              </div>
            );
          })}
        </section>

        <aside className="of-bom card" aria-label="BOM">
          <div className="of-bom-head">
            <h2>BOM</h2>
            {listQ.data && <span className="muted">{stdCount} standard + {optCount} option sections · {lines.length} lines · {parts.length} parts</span>}
          </div>
          {!bomSeason && bomQ.data && (
            <p className="muted">There’s no BOM list for the {model} yet. {can('bom:manage') ? <Link to="/bom-lists">Upload the STD lists</Link> : 'Someone who manages the BOM lists can upload the STD lists'}; the order can still be saved meanwhile.</p>
          )}
          {listQ.isLoading && <LoadingState />}
          {listQ.data && (
            <>
              <p className="muted of-bom-src">From {bomFrom}{bomSeason !== season ? ` (the latest list before season ${season})` : ''}. Tick a section in or out for this boat: the BOM file is the model’s sheet with the sections left out deleted. The rules are on the <Link to="/bom-lists">BOM lists</Link> page.</p>
              <BomSections title="Options" rows={resolved.filter((r) => r.section.part === 'opt')} canWrite={canWrite} onPick={pick} />
              <BomSections title="Standard" rows={resolved.filter((r) => r.section.part === 'std')} canWrite={canWrite} onPick={pick} />
            </>
          )}
        </aside>
      </div>
    </>
  );
}

function Back() {
  return (
    <Link to="/info-files" className="adm-back">
      <IconChevronRight size={16} strokeWidth={2.2} />
      Info / Files
    </Link>
  );
}

/** a part of the BOM: its sections grouped as the sheet groups them, each ticked in or out */
function BomSections({ title, rows, canWrite, onPick }: { title: string; rows: ResolvedBomSection[]; canWrite: boolean; onPick: (key: string, on: boolean | undefined) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!rows.length) return null;
  let group = '';
  let prevRef = '';
  return (
    <div className="of-bom-part">
      <h3>{title} <span className="muted">· {rows.filter((r) => r.included).length} of {rows.length}</span></h3>
      <ul className="of-secs">
        {rows.map((r) => {
          const s = r.section;
          const head = s.group && s.group !== group ? (group = s.group) : null;
          const hand = r.picked !== undefined;
          // a standard heading that shares the code of the one before is its subsection, as the STD list files it
          const sub = s.part === 'std' && Boolean(s.ref) && s.ref === prevRef;
          prevRef = s.ref;
          return (
            <li key={s.key} className={`of-sec${r.included ? ' is-in' : ''}${sub ? ' is-sub' : ''}`}>
              {head && <div className="of-sec-group">{head}</div>}
              <div className="of-sec-row">
                <input
                  type="checkbox"
                  checked={r.included}
                  disabled={!canWrite}
                  aria-label={`${s.title} on this boat`}
                  onChange={(e) => onPick(s.key, e.target.checked === r.auto ? undefined : e.target.checked)}
                />
                <button type="button" className="of-sec-t" aria-expanded={open === s.key} onClick={() => setOpen(open === s.key ? null : s.key)}>
                  {sub && <span className="muted" aria-hidden="true">↳ </span>}{s.title} <span className="muted">({s.items.length})</span>
                </button>
                {hand && canWrite && <button type="button" className="btn btn-sm btn-ghost" title="Back to what its rule says" onClick={() => onPick(s.key, undefined)}>Undo</button>}
              </div>
              <div className="of-sec-why muted">{hand ? `Ticked ${r.picked ? 'in' : 'out'} by hand · rule: ${bomLinkText(r.link)}` : s.part === 'std' && r.link.mode === 'always' ? 'Standard' : bomLinkText(r.link)}</div>
              {open === s.key && (
                <table className="of-items">
                  <tbody>
                    {s.items.map((i, n) => (
                      <tr key={n}><td className="mono">{i.code || '-'}</td><td>{i.desc}{i.note && <span className="muted"> · {i.note}</span>}</td><td className="num">{i.qty ?? i.qtyText}</td><td className="muted">{i.box}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
